import { useState, useEffect, useRef, useCallback } from "react";
import { ShieldCheck, CheckCircle2, Mail, Loader2, ExternalLink } from "lucide-react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Aperçu RESPONSIVE de l'e-mail : rendu à sa largeur logique standard (640px) puis mis à
// l'échelle pour tenir EXACTEMENT dans la largeur du dialogue (aucun rognage à droite).
// sandbox="allow-same-origin" (SANS allow-scripts) : scripts non exécutés (sûr), mais on
// peut mesurer la hauteur réelle pour éviter tout espace vide / coupe verticale.
function ProofEmailPreview({ html }) {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const EMAIL_W = 640;
  const [scale, setScale] = useState(1);
  const [logicalH, setLogicalH] = useState(560);

  const recompute = useCallback(() => {
    const box = boxRef.current;
    if (box) {
      const w = box.clientWidth || EMAIL_W;
      setScale(Math.min(1, w / EMAIL_W));
    }
    const frame = frameRef.current;
    try {
      const doc = frame && (frame.contentDocument || frame.contentWindow?.document);
      if (doc && doc.body) {
        setLogicalH(Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, 200));
      }
    } catch { /* accès impossible : on conserve la hauteur par défaut */ }
  }, []);

  useEffect(() => {
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [recompute, html]);

  const scaledH = Math.ceil(logicalH * scale);
  return (
    <div ref={boxRef} className="w-full bg-white">
      <div className="max-h-80 overflow-y-auto overflow-x-hidden">
        <div className="overflow-hidden" style={{ height: scaledH }}>
          <iframe
            ref={frameRef}
            title="E-mail de confirmation"
            srcDoc={html}
            sandbox="allow-same-origin"
            onLoad={recompute}
            data-testid="spot-proof-iframe"
            style={{ width: EMAIL_W, height: logicalH, transform: `scale(${scale})`, transformOrigin: "top left", border: 0, display: "block" }}
          />
        </div>
      </div>
    </div>
  );
}

function Facts({ data }) {
  const rows = [["Magasin", data.facts?.magasin], ["Adresse", data.facts?.adresse], ["Dates", data.facts?.dates],
    ["Association", data.facts?.association], ["Responsable", data.facts?.responsable_nom], ["Envoyé à", data.to]]
    .filter(([, v]) => v);
  if (rows.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-1.5 rounded-xl border border-[var(--border-soft)] p-3 text-[13px]">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{k}</span>
          <span className="min-w-0 break-words font-medium text-ink">{v}</span>
        </div>
      ))}
    </div>
  );
}

// Dialogue « Preuve de confirmation » d'un spot — à MONTRER sur le terrain au magasin.
// Logique « OU » :
//   - si le magasin a CONFIRMÉ (accusé signé) → on montre LA preuve de confirmation
//     (bandeau vert + attestation PDF), c'est ce qui fait foi ;
//   - sinon → on montre simplement l'APERÇU de l'e-mail de confirmation envoyé.
// Lecture seule (n'envoie aucun e-mail). `load` : () => Promise<data>.
export function SpotProofDialog({ open, onOpenChange, spotName, load }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setErr(null); setData(null);
    Promise.resolve(loadRef.current())
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Impossible de charger la preuve de confirmation."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }); } catch { return iso || "—"; } };
  const ackToken = data?.ack_token || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto [&>*]:min-w-0" data-testid="spot-proof-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-[var(--shariti-orange)]" />
            Preuve de confirmation
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-ink" data-testid="spot-proof-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
          </div>
        )}
        {!loading && err && (
          <div className="rounded-xl bg-[rgba(214,69,69,0.08)] px-3 py-3 text-sm font-medium text-[#9B2226]" data-testid="spot-proof-error">{err}</div>
        )}
        {!loading && data && !data.found && (
          <div className="rounded-xl border border-dashed border-[var(--border-soft)] px-3 py-6 text-center text-sm text-muted-ink" data-testid="spot-proof-empty">
            Aucun e-mail de confirmation ni accusé de réception n'a encore été enregistré pour <span className="font-semibold text-ink">{spotName || "ce spot"}</span>.
          </div>
        )}

        {!loading && data && data.found && (
          <div className="space-y-3">
            {data.acknowledged ? (
              // CONFIRMÉ par le magasin : la preuve ferme qui fait foi (pas d'e-mail affiché).
              <>
                <div className="flex items-start gap-2 rounded-xl bg-[rgba(31,122,68,0.1)] px-3 py-2.5 text-[#1F7A44]" data-testid="spot-proof-ack">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold">Réception confirmée par le magasin</div>
                    <div className="text-[12px] font-medium opacity-90">
                      Accusé de réception le {fmtDT(data.acknowledged_at)}
                      {data.director_name ? ` — par ${data.director_name}` : ""}. C'est la preuve qui fait foi.
                    </div>
                  </div>
                </div>
                <Facts data={data} />
                {ackToken && (
                  <a href={`${API}/ack/public/${ackToken}/attestation.pdf`} target="_blank" rel="noreferrer"
                    data-testid="spot-proof-pdf"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#1F7A44] transition-colors hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> Télécharger l'attestation (PDF)
                  </a>
                )}
              </>
            ) : data.sent ? (
              // AU CRAYON (pas encore confirmé) : on montre l'aperçu de l'e-mail envoyé.
              <>
                <div className="flex items-start gap-2 rounded-xl bg-[rgba(238,114,46,0.1)] px-3 py-2.5 text-[#9A4B12]" data-testid="spot-proof-sent">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold">E-mail de confirmation envoyé au magasin</div>
                    <div className="text-[12px] font-medium opacity-90">
                      Envoyé le {fmtDT(data.sent_at)}. À présenter à l'accueil en cas de besoin.
                    </div>
                  </div>
                </div>
                <Facts data={data} />
                {data.body_html && (
                  <div className="overflow-hidden rounded-xl border border-[var(--border-soft)]">
                    <div className="line-clamp-2 break-words border-b border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-1.5 text-[11px] font-semibold leading-snug text-muted-ink">{data.subject}</div>
                    <ProofEmailPreview html={data.body_html} />
                  </div>
                )}
              </>
            ) : (
              <Facts data={data} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Bouton DISCRET « Preuve de confirmation » à poser sur une carte de spot terrain.
// `load` fournit la donnée (via le lien du collaborateur / responsable).
export function SpotProofButton({ spotName, load, testid = "spot-proof-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} aria-expanded={open}
        className="mt-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-muted-ink transition-colors hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
        title="Montrer la preuve de confirmation au magasin (accusé signé ou e-mail envoyé)">
        <ShieldCheck className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
        Preuve de confirmation
      </button>
      <SpotProofDialog open={open} onOpenChange={setOpen} spotName={spotName} load={load} />
    </>
  );
}

export default SpotProofButton;
